// react
import React, { useEffect, useRef, useState } from 'react'

// components
import Header from '_components/header/index.jsx'
import ListView from '_components/list-view/index.jsx'
import ModalCreateIdSet from './modal-create-id-set'

// services
import {
  createTaxonomyIdSet,
  getTaxonomyIdSets,
  waitForTaxonomyIdSetFill,
} from '_services/taxonomy-id-sets'

// Segment Ids Page
//
// Manages the partner-specific segment ID formats offered to every taxonomy's
// Segment IDs dropdown. Creating a set here defines the format; audiences pick
// up their IDs as they are created, and existing ones through Backfill on the
// set itself.
// how often to re-check whether anything is still filling
const WATCH_INTERVAL_MS = 5000

const SegmentIdsPage = () => {
  const [isCreateOpen, setCreateOpen] = useState(false)

  // how far the fill behind the open dialog has got, so the spinner can show
  // something real rather than an indeterminate wait
  const [fillProgress, setFillProgress] = useState(null)

  // ListView loads and holds its own page of data, so it is remounted rather
  // than asked to refetch
  const [listKey, setListKey] = useState(0)

  // bumped to (re)start the watcher below
  const [watchToken, setWatchToken] = useState(0)

  // A fill outlives the dialog, and ListView never re-fetches on its own - so a
  // fill that finished after the page rendered would leave its row reading
  // "running" until a manual reload, which is indistinguishable from one that
  // died. Watch while anything is still filling and remount the list whenever
  // that set of rows changes.
  const runningRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    let timer = null

    const tick = async () => {
      if (cancelled) return

      try {
        // listing is itself what corrects a fill that has gone quiet - the API
        // does that before answering - so the statuses here are already true
        const data = await getTaxonomyIdSets()
        const items = data?.items ?? []

        const running = items
          .filter((set) => set.fill_status === 'running')
          .map((set) => set._id)
          .sort()
          .join(',')

        // only remount when the picture actually changed, so an untouched list
        // is not torn down every few seconds
        if (runningRef.current !== null && running !== runningRef.current) {
          setListKey((key) => key + 1)
        }
        runningRef.current = running

        // nothing left to watch
        if (!running) return
      } catch (error) {
        // keep watching - failing to observe a fill says nothing about it
      }

      timer = setTimeout(tick, WATCH_INTERVAL_MS)
    }

    tick()

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [watchToken])

  // Shows what the server holds now, and picks the watcher back up: it stops
  // once nothing is filling, and whatever prompted the refresh may have started
  // something.
  const refreshList = () => {
    setListKey((key) => key + 1)

    // the rows we are about to fetch are the watcher's new starting point, so
    // its first tick has nothing to correct
    runningRef.current = null
    setWatchToken((token) => token + 1)
  }

  // Creating a set answers as soon as the set exists. Giving every segment that
  // already exists an ID carries on server-side afterwards - it outlasts the
  // load balancer's patience on a real database - so the dialog follows the
  // set's own fill_status instead of holding a request open.
  const handleCreate = async (payload) => {
    const idSet = await createTaxonomyIdSet(payload)
    const name = idSet?.name ?? payload.name

    // The set exists as of this line, and the dialog invites closing it while
    // the fill carries on. Show the row now: waiting for the fill to end leaves
    // anyone who takes that invitation looking at a list their new set is
    // missing from, for as long as the fill runs, with only a reload to fix it.
    refreshList()

    setFillProgress(0)
    const { idSet: filled, timedOut } = await waitForTaxonomyIdSetFill(idSet._id, {
      onPoll: (current) => setFillProgress(current?.filled_count ?? 0),
    })
    setFillProgress(null)

    // the fill has stopped - show where it landed
    refreshList()

    if (timedOut) {
      return toast.success(
        `Created "${name}". It is still generating IDs for existing segments — the list shows when it finishes.`
      )
    }

    if (filled?.fill_status === 'failed') {
      return toast.error(
        `Created "${name}", but generating IDs for existing segments failed: ${
          filled.fill_error || 'unknown error'
        }`
      )
    }

    toast.success(`Created "${name}" and gave ${filled?.filled_count ?? 0} existing segments an ID`)
  }

  return (
    <div id="segment-ids-page">
      <Header
        title="Segment Ids"
        actions={[
          {
            children: 'Create New Segment ID Set',
            onClick: () => setCreateOpen(true),
          },
        ]}
      />

      <ListView
        key={listKey}
        columns={[
          {
            key: 'name',
            link: '/segment-ids/:_id',
            sortable: true,
          },
          {
            key: 'description',
            name: 'Description',
          },
          {
            // the key this set occupies on an audience, and the value a
            // distribution stores in mapping_ids
            key: 'slug',
            name: 'Mapping Space',
          },
          {
            key: 'size',
            name: 'Characters',
          },
          {
            // visible because the fill outlives the dialog: a set can still be
            // working through existing segments after its row appears
            key: 'fill_status',
            name: 'Fill',
          },
          {
            // how far it actually got - a running fill whose count is climbing
            // is alive, and a finished one shows what it covered
            key: 'filled_count',
            name: 'IDs Generated',
          },
        ]}
        entities="taxonomy-id-sets"
        fields={['name', 'description', 'slug', 'size', 'fill_status', 'filled_count']}
        searchable={true}
        sort_key={'name'}
        sort_dir={1}
      />

      <ModalCreateIdSet
        isOpen={isCreateOpen}
        closeModal={() => setCreateOpen(false)}
        create={handleCreate}
        progress={fillProgress}
      />
    </div>
  )
}

// export
export default SegmentIdsPage
